/*
Copyright 2025 TekNix Corporation

This software is proprietary and confidential to TekNix Corporation.
All rights reserved. No part of this software may be reproduced, distributed,
or transmitted in any form or by any means, including photocopying, recording,
or other electronic or mechanical methods, without the prior written permission
of TekNix Corporation, except in the case of brief quotations embodied in
critical reviews and certain other noncommercial uses permitted by copyright law.

For permission requests, write to TekNix Corporation at the address below:
TekNix Corporation
Legal Department

UNAUTHORIZED REPRODUCTION OR DISTRIBUTION IS STRICTLY PROHIBITED.
*/

import type { IMinimalEvent } from '../@types/event';
import type { RoomData } from '../sync/interface';
import type { IIndexedDBBackend, UserTuple } from './interfaces/indexeddb-backend';
import type { IStoredClientOpts } from './interfaces/store';

import { deepCopy, promiseTry } from '../utils';
import { logger } from '../utils/logger';
import isIdbExists from './helper/isExit';
import { reqAsEventPromise, txnAsPromise } from './helper/promise';
import selectQuery from './helper/selectQuery';
import { hashUserId } from './helper/user-hash';

const DB_NAME_PREFIX = 'xhub-chat-sdk';

type DbMigration = (db: IDBDatabase) => void;
const DB_MIGRATIONS: DbMigration[] = [
  (db): void => {
    // Make user store, clobber based on user ID. (userId property of User objects)
    // Note: Contacts are also stored as users in this store
    db.createObjectStore('users', { keyPath: ['userId'] });
    // Make account data store, clobber based on event type.
    db.createObjectStore('accountData', { keyPath: ['type'] });
    // Make room store, clobber based on room ID. (roomId property of Room objects)
    db.createObjectStore('client_options', { keyPath: ['clobber'] });
    // Make rooms store, clobber based on room ID. (roomId property of Room objects)
    const roomsStore = db.createObjectStore('rooms', { keyPath: 'id' });
    roomsStore.createIndex('id', 'id', { unique: false });
  },
  // Migration 2: Add user_metadata store for storing current userId
  (db): void => {
    // Store metadata about which user owns this database
    db.createObjectStore('user_metadata', { keyPath: 'key' });
  },
  // Expand as needed.
];
const VERSION = DB_MIGRATIONS.length;

export class LocalIndexedDBStoreBackend implements IIndexedDBBackend {
  public static exists(indexedDB: IDBFactory, dbName: string, userId?: string): Promise<boolean> {
    const userHash = userId ? hashUserId(userId) : 'default';
    dbName = `${DB_NAME_PREFIX}:${dbName || 'default'}:${userHash}`;
    return isIdbExists(indexedDB, dbName);
  }

  /**
   * Whether or not the database was newly created in this session.
   */
  private _isNewlyCreated = false;

  private disconnected = true;
  private readonly dbName: string;
  private readonly userId?: string;

  private syncToDatabasePromise?: Promise<void>;
  private pendingUserPresenceData: UserTuple[] = [];
  private onCloseCallback?: () => void;

  private roomDataCache: Record<string, RoomData> = {};

  /**
   * The IndexedDB database instance. This will be `undefined` if not
   */
  private db?: IDBDatabase;

  /**
   * Does the actual reading from and writing to the indexeddb
   *
   * Construct a new Indexed Database store backend. This requires a call to
   * `connect()` before this store can be used.
   * @param indexedDB - The Indexed DB interface e.g
   * `window.indexedDB`
   * @param dbName - Optional database name. The same name must be used
   * to open the same database.
   * @param userId - Optional user ID for database isolation. When provided,
   * creates a separate database per user to prevent data leaks.
   */
  public constructor(
    private readonly indexedDB: IDBFactory,
    dbName = 'default',
    userId?: string,
  ) {
    this.userId = userId;
    // Hash userId to create a safe database name component
    const userHash = userId ? hashUserId(userId) : 'default';
    this.dbName = `${DB_NAME_PREFIX}:${dbName}:${userHash}`;
  }

  /** @returns whether or not the database was newly created in this session. */
  public isNewlyCreated(): Promise<boolean> {
    return Promise.resolve(this._isNewlyCreated);
  }

  /**
   * Attempt to connect to the database. This can fail if the user does not
   * grant permission.
   * @returns Promise which resolves if successfully connected.
   */
  public async connect(onClose?: () => void): Promise<void> {
    if (onClose) {
      this.onCloseCallback = onClose;
    }

    if (!this.disconnected) {
      logger.log(`LocalIndexedDBStoreBackend.connect: already connected or connecting`);
      return Promise.resolve();
    }

    this.disconnected = false;

    // Defensive check: Ensure IndexedDB API is available.
    if (!this.indexedDB) {
      logger.error('LocalIndexedDBStoreBackend.connect: IndexedDB is not supported or available in this environment.');
      return Promise.reject(new Error('IndexedDB not supported'));
    }

    logger.log(`LocalIndexedDBStoreBackend.connect: connecting...`);
    const req = this.indexedDB.open(this.dbName, VERSION);

    req.onupgradeneeded = (ev): void => {
      const db = req.result;
      const oldVersion = ev.oldVersion;
      logger.log(`LocalIndexedDBStoreBackend.connect: upgrading from ${oldVersion}`);
      if (oldVersion < 1) {
        // The database did not previously exist
        this._isNewlyCreated = true;
      }
      DB_MIGRATIONS.forEach((migration, index) => {
        if (oldVersion <= index) migration(db);
      });
    };

    req.onblocked = (): void => {
      logger.log(`can't yet open LocalIndexedDBStoreBackend because it is open elsewhere`);
    };

    logger.log(`LocalIndexedDBStoreBackend.connect: awaiting connection...`);
    return reqAsEventPromise(req).then(async () => {
      this.db = req.result;

      // add a poorly-named listener for when deleteDatabase is called
      // so we can close our db connections.
      this.db.onversionchange = (): void => {
        this.db?.close(); // this does not call onclose
        this.disconnected = true;
        this.db = undefined;
      };
      this.db.onclose = (): void => {
        this.disconnected = true;
        this.db = undefined;
        onClose?.();
      };

      await this.init();
    });
  }

  /**
   * Having connected, load initial data from the database and prepare for use
   * @returns Promise which resolves on success
   */
  private async init(): Promise<unknown> {
    // Validate user ownership if userId is provided
    if (this.userId) {
      await this.validateUserOwnership();
    }

    return Promise.all([this.loadAccountData(), this.loadRoomsData()]).then(([accountData, roomsData]) => {
      logger.log(`LocalIndexedDBStoreBackend: loaded initial data`, [accountData, roomsData]);
      this.roomDataCache = roomsData.reduce((acc, room) => {
        acc[room.id] = room;
        return acc;
      }, {} as Record<string, RoomData>);
      return Promise.resolve();
    });
  }

  /**
   * Validate that the current user owns this database.
   * If the database belongs to a different user, clear it.
   */
  private async validateUserOwnership(): Promise<void> {
    if (!this.userId) return;

    try {
      const storedUserId = await this.getStoredUserId();

      if (storedUserId && storedUserId !== this.userId) {
        logger.warn(
          `User mismatch detected! Database belongs to ${storedUserId}, but current user is ${this.userId}. `
          + `Clearing database to prevent data leak...`,
        );

        // Clear all data to prevent data leak
        await this.clearAllUserData();

        // Store the new userId
        await this.storeUserId(this.userId);
      } else if (!storedUserId) {
        // First time using this database, store userId
        logger.info(`Initializing database for user: ${this.userId}`);
        await this.storeUserId(this.userId);
      } else {
        logger.info(`User ownership validated: ${this.userId}`);
      }
    } catch (error) {
      logger.error('Failed to validate user ownership:', error);
      // On error, clear database to be safe
      await this.clearAllUserData();
      if (this.userId) {
        await this.storeUserId(this.userId);
      }
    }
  }

  /**
   * Get the stored userId from the database metadata.
   * @returns The userId that owns this database, or null if not set.
   */
  private async getStoredUserId(): Promise<string | null> {
    if (!this.db) {
      return null;
    }

    try {
      const txn = this.db.transaction(['user_metadata'], 'readonly');
      const store = txn.objectStore('user_metadata');
      const req = store.get('current_user_id');

      return new Promise((resolve, reject) => {
        req.onsuccess = (): void => {
          resolve(req.result?.value || null);
        };
        req.onerror = (): void => {
          reject(req.error);
        };
      });
    } catch (error) {
      logger.warn('Failed to get stored userId:', error);
      return null;
    }
  }

  /**
   * Store the current userId in the database metadata.
   * @param userId - The userId to store.
   */
  private async storeUserId(userId: string): Promise<void> {
    if (!this.db) {
      return;
    }

    try {
      const txn = this.db.transaction(['user_metadata'], 'readwrite');
      const store = txn.objectStore('user_metadata');
      store.put({
        key: 'current_user_id',
        value: userId,
        updated_at: Date.now(),
      });
      await txnAsPromise(txn);
      logger.info(`Stored userId in database: ${userId}`);
    } catch (error) {
      logger.error('Failed to store userId:', error);
      throw error;
    }
  }

  /**
   * Clear all user-specific data from the database.
   * This is called when a user mismatch is detected.
   */
  private async clearAllUserData(): Promise<void> {
    if (!this.db) {
      return;
    }

    try {
      logger.warn('Clearing all user data due to user mismatch...');

      const txn = this.db.transaction(['users', 'accountData', 'rooms', 'client_options'], 'readwrite');

      // Clear all object stores
      txn.objectStore('users').clear();
      txn.objectStore('accountData').clear();
      txn.objectStore('rooms').clear();
      txn.objectStore('client_options').clear();

      await txnAsPromise(txn);

      // Clear in-memory caches
      this.roomDataCache = {};
      this.pendingUserPresenceData = [];

      logger.info('All user data cleared successfully');
    } catch (error) {
      logger.error('Failed to clear user data:', error);
      throw error;
    }
  }

  /**
   * ======================================================================
   *                           LOADING FROM CACHE
   * ======================================================================
   */

  /**
   * @param copy - If false, the data returned is from internal
   * buffers and must not be mutated. Otherwise, a copy is made before
   * returning such that the data can be safely mutated. Default: true.
   *
   * @returns Promise which resolves with a sync response to restore the
   * client state to where it was at the last save, or null if there
   * is no saved sync data.
   */
  public getSavedRooms(copy = true): Promise<RoomData[] | null> {
    const data = this.roomDataCache;
    if (copy) {
      // We must deep copy the stored data so that the /sync processing code doesn't
      // corrupt the internal state of the sync accumulator (it adds non-clonable keys)
      return Promise.resolve(deepCopy(data ? Object.values(data) : []));
    } else {
      return Promise.resolve(data ? Object.values(data) : []);
    }
  }

  /**
   * Load all the account data events from the database. This is not cached.
   * @returns A list of raw global account events.
   */
  private loadAccountData(): Promise<IMinimalEvent[]> {
    logger.log(`LocalIndexedDBStoreBackend: loading account data...`);
    const onResult = (cursor: IDBCursorWithValue) => cursor.value;
    return promiseTry<IMinimalEvent[]>(async () => {
      const txn = this.db!.transaction(['accountData'], 'readonly');
      const store = txn.objectStore('accountData');
      return selectQuery(store, undefined, onResult).then((result: IMinimalEvent[]) => {
        logger.log(`LocalIndexedDBStoreBackend: loaded account data`);
        return result;
      });
    });
  }

  /**
   * Load the rooms data from the database.
   * @returns An object with "roomsData" and "nextBatch" keys.
   */
  private loadRoomsData(): Promise<RoomData[]> {
    logger.log(`LocalIndexedDBStoreBackend: loading rooms data...`);
    return promiseTry<RoomData[]>(async () => {
      const txn = this.db!.transaction(['rooms'], 'readonly');
      const store = txn.objectStore('rooms');
      return selectQuery(store, undefined, (cursor) => {
        return cursor.value;
      }).then((results: RoomData[]) => {
        logger.log(`LocalIndexedDBStoreBackend: loaded rooms data`);
        if (results.length < 0) {
          logger.warn('loadRoomsData: No rooms found.');
        }
        return results.sort((a, b) => new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime());
      });
    });
  }
  /**
   * ======================================================================
   *                          END LOADING FROM CACHE
   * ======================================================================
   */

  /**
   * Persist a list of [user id, presence event] they are for.
   * Users with the same 'userId' will be replaced.
   * Presence events should be the event in its raw form (not the Event
   * object)
   * @param tuples - An array of [userid, event] tuples
   * @returns Promise which resolves if the users were persisted.
   */
  private persistUserPresenceEvents(tuples: UserTuple[]): Promise<void> {
    return promiseTry<void>(async () => {
      const txn = this.db!.transaction(['users'], 'readwrite');
      const store = txn.objectStore('users');
      for (const tuple of tuples) {
        store.put({
          userId: tuple[0],
          event: tuple[1],
          userData: tuple[2],
        }); // put == UPSERT
      }
      return txnAsPromise(txn).then();
    });
  }

  /**
   * Persist rooms /sync data along with the next batch token.
   * @param nextBatch - The next_batch /sync value.
   * @param roomsData - The 'rooms' /sync data from a SyncAccumulator
   * @returns Promise which resolves if the data was persisted.
   */
  private persistRoomsData(nextBatch: string, roomsData: RoomData[]): Promise<void> {
    logger.log('Persisting rooms data up to', nextBatch);
    return promiseTry<void>(async () => {
      const txn = this.db!.transaction(['rooms'], 'readwrite');
      const store = txn.objectStore('rooms');
      await Promise.all([
        ...roomsData.map(room => store.put(room)),
      ]);
      return txnAsPromise(txn).then(() => {
        logger.log('Persisted rooms data up to', nextBatch);
      });
    });
  }

  /**
   * Sync users and all accumulated sync data to the database.
   * If a previous sync is in flight, the new data will be added to the
   * next sync and the current sync's promise will be returned.
   * @param userTuples - The user tuples
   * @returns Promise which resolves if the data was persisted.
   */
  public async syncToDatabase(userTuples: UserTuple[]): Promise<void> {
    if (this.syncToDatabasePromise) {
      logger.warn('Skipping syncToDatabase() as persist already in flight');
      if (userTuples.length) {
        this.pendingUserPresenceData.push(...userTuples);
      }
      return this.syncToDatabasePromise;
    }

    userTuples.unshift(...this.pendingUserPresenceData);
    this.syncToDatabasePromise = this.doSyncToDatabase(userTuples);
    return this.syncToDatabasePromise;
  }

  private async doSyncToDatabase(userTuples: UserTuple[]): Promise<void> {
    const roomsData = this.roomDataCache;

    try {
      await Promise.all([
        this.persistUserPresenceEvents(userTuples),
        this.persistRoomsData('TODO: Need Implement Later!', Object.values(roomsData)),
      ]);
    } catch (error) {
      if (this.isInvalidStateError(error)) {
        logger.warn('LocalIndexedDBStoreBackend: database connection closing detected; attempting to reopen');
        await this.retrySyncAfterReconnect(userTuples);
        return;
      }
      this.pendingUserPresenceData.unshift(...userTuples);
      throw error;
    } finally {
      this.syncToDatabasePromise = undefined;
    }
  }

  /**
   * Set the rooms data. This does not persist it to the database.
   * @param roomsData - The room data to set.
   * @returns An immediately resolved promise.
   */
  public setRoomsData(roomsData: Record<string, RoomData>): Promise<void> {
    this.roomDataCache = { ...this.roomDataCache, ...roomsData };
    return Promise.resolve();
  }

  private async retrySyncAfterReconnect(userTuples: UserTuple[]): Promise<void> {
    this.pendingUserPresenceData.unshift(...userTuples);
    await this.reopenDatabase();

    const retryTuples = this.pendingUserPresenceData.splice(0);
    if (retryTuples.length === 0) {
      return;
    }

    try {
      await this.persistUserPresenceEvents(retryTuples);
    } catch (error) {
      this.pendingUserPresenceData.unshift(...retryTuples);
      throw error;
    }
  }

  private async reopenDatabase(): Promise<void> {
    if (this.db) {
      try {
        this.db.close();
      } catch (error) {
        logger.warn('LocalIndexedDBStoreBackend: error closing db during reopen', error);
      }
      this.db = undefined;
    }

    this.disconnected = true;
    await this.connect(this.onCloseCallback);
  }

  private isInvalidStateError(error: unknown): boolean {
    if (typeof DOMException !== 'undefined' && error instanceof DOMException) {
      return error.name === 'InvalidStateError';
    }

    if (error && typeof error === 'object' && 'name' in error) {
      return (error as { name?: string }).name === 'InvalidStateError';
    }

    return false;
  }

  /**
   * ======================================================================
   *                          CLIENT OPTIONS
   * ======================================================================
   */
  public async getClientOptions(): Promise<IStoredClientOpts | undefined> {
    return Promise.resolve().then(async () => {
      const txn = this.db!.transaction(['client_options'], 'readonly');
      const store = txn.objectStore('client_options');
      return selectQuery(store, undefined, (cursor) => {
        return cursor.value?.options;
      }).then(results => results[0]);
    });
  }

  public async storeClientOptions(options: IStoredClientOpts): Promise<void> {
    const txn = this.db!.transaction(['client_options'], 'readwrite');
    const store = txn.objectStore('client_options');
    store.put({
      clobber: '-', // constant key so will always clobber
      options,
    }); // put == UPSERT
    await txnAsPromise(txn);
  }
  /**
   * ======================================================================
   *                          END CLIENT OPTIONS
   * ======================================================================
   */

  /**
   * Clear the entire database. This should be used when logging out of a client
   * to prevent mixing data between accounts. Closes the database.
   * @returns Resolved when the database is cleared.
   */
  public clearDatabase(): Promise<void> {
    return new Promise((resolve) => {
      logger.info(`Removing indexeddb instance: ${this.dbName}`);

      // Close the database first to avoid firing unexpected close events
      this.db?.close();
      this.db = undefined;
      this.disconnected = true;
      const req = this.indexedDB.deleteDatabase(this.dbName);

      req.onblocked = (): void => {
        logger.log(`can't yet delete indexeddb ${this.dbName} because it is open elsewhere`);
      };

      req.onerror = (): void => {
        // in firefox, with indexedDB disabled, this fails with a
        // DOMError. We treat this as non-fatal, so that we can still
        // use the app.
        logger.warn(`unable to delete js-sdk store indexeddb: ${req.error?.name}`);
        resolve();
      };

      req.onsuccess = (): void => {
        logger.log(`Removed indexeddb instance: ${this.dbName}`);
        resolve();
      };
    });
  }

  /**
   * Load all user presence events from the database. This is not cached.
   * FIXME: It would probably be more sensible to store the events in the
   * sync.
   * @returns A list of presence events in their raw form.
   */
  public getUserPresenceEvents(): Promise<UserTuple[]> {
    return promiseTry<UserTuple[]>(() => {
      const txn = this.db!.transaction(['users'], 'readonly');
      const store = txn.objectStore('users');
      return selectQuery(store, undefined, (cursor) => {
        return [cursor.value.userId, cursor.value.event, cursor.value.userData];
      });
    });
  }

  /*
     * Close the database
     */
  public async destroy(): Promise<void> {
    this.db?.close();
    this.db = undefined;
    this.disconnected = true;
  }
}
